import type { AxiosResponse } from 'axios'
import { createContext, useContext } from 'react'

import { PBPGetResponse } from '@percent/utility'

import {
  GetOrganisationProfilesParams,
  OrganisationProfile,
  OrganisationProfileCountry,
  OrganisationProfileLinks,
  OrganisationProfilesResponse,
  RenderOrganisationProfileLink,
} from '../types/organisationProfile.types'

// Host-supplied HTTP boundary; the lib never constructs its own client.
export type OrganisationProfileService = {
  getOrganisationProfiles: (
    params: GetOrganisationProfilesParams
  ) => Promise<AxiosResponse<OrganisationProfilesResponse>>
  getOrganisationProfile: (params: {
    organisationId: string
  }) => Promise<AxiosResponse<PBPGetResponse<OrganisationProfile>>>
}

export type OrganisationProfileContextValue = {
  service: OrganisationProfileService
  locale: string
  countries: OrganisationProfileCountry[]
  links: OrganisationProfileLinks
  renderLink?: RenderOrganisationProfileLink
}

export const OrganisationProfileContext = createContext<
  OrganisationProfileContextValue | undefined
>(undefined)

OrganisationProfileContext.displayName = 'OrganisationProfileContext'

export const useOrganisationProfileContext =
  (): OrganisationProfileContextValue => {
    const context = useContext(OrganisationProfileContext)

    if (!context) {
      throw new Error(
        'useOrganisationProfileContext must be used within an <OrganisationProfile.Provider>'
      )
    }

    return context
  }
